const API_URL = process.env.REACT_APP_BACKEND_URL;

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
    throw new Error(error.detail || 'An error occurred');
  }
  return response.json();
};

const fetchWithAuth = async (endpoint, options = {}) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('session_token') : null;
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  return handleResponse(response);
};

// HOA API
export const hoaApi = {
  create: (data) => fetchWithAuth('/hoa/create', { method: 'POST', body: JSON.stringify(data) }),
  list: () => fetchWithAuth('/hoa/list'),
  get: (hoaId) => fetchWithAuth(`/hoa/${hoaId}`),
  update: (hoaId, data) => fetchWithAuth(`/hoa/${hoaId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getStats: (hoaId) => fetchWithAuth(`/hoa/stats/${hoaId}`),
  listUnits: (hoaId) => fetchWithAuth(`/hoa/units/${hoaId}`),
  createUnit: (data) => fetchWithAuth('/hoa/units/create', { method: 'POST', body: JSON.stringify(data) }),
  bulkImportUnits: (data) => fetchWithAuth('/hoa/units/bulk-import', { method: 'POST', body: JSON.stringify(data) }),
};

// Notices API
export const noticesApi = {
  create: (data) => fetchWithAuth('/notices/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, category = null) => fetchWithAuth(`/notices/list/${hoaId}${category ? `?category=${category}` : ''}`),
  get: (noticeId) => fetchWithAuth(`/notices/${noticeId}`),
  update: (noticeId, data) => fetchWithAuth(`/notices/${noticeId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (noticeId) => fetchWithAuth(`/notices/${noticeId}`, { method: 'DELETE' }),
  generateAI: (data) => fetchWithAuth('/notices/generate-ai', { method: 'POST', body: JSON.stringify(data) }),
};

// Documents API
export const documentsApi = {
  upload: (data) => fetchWithAuth('/documents/upload', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, docType = null) => fetchWithAuth(`/documents/list/${hoaId}${docType ? `?doc_type=${docType}` : ''}`),
  get: (documentId) => fetchWithAuth(`/documents/${documentId}`),
  delete: (documentId) => fetchWithAuth(`/documents/${documentId}`, { method: 'DELETE' }),
  search: (hoaId, query) => fetchWithAuth(`/documents/search/${hoaId}`, { method: 'POST', body: JSON.stringify({ query }) }),
  summarize: (documentId) => fetchWithAuth(`/documents/summarize/${documentId}`, { method: 'POST' }),
};

// Maintenance API
export const maintenanceApi = {
  create: (data) => fetchWithAuth('/maintenance/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, status = null, category = null) => {
    let url = `/maintenance/list/${hoaId}`;
    const params = [];
    if (status) params.push(`status=${status}`);
    if (category) params.push(`category=${category}`);
    if (params.length) url += `?${params.join('&')}`;
    return fetchWithAuth(url);
  },
  get: (ticketId) => fetchWithAuth(`/maintenance/${ticketId}`),
  update: (ticketId, data) => fetchWithAuth(`/maintenance/${ticketId}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateStatus: (ticketId, data) => fetchWithAuth(`/maintenance/${ticketId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (ticketId) => fetchWithAuth(`/maintenance/${ticketId}`, { method: 'DELETE' }),
  getStats: (hoaId) => fetchWithAuth(`/maintenance/stats/${hoaId}`),
};

// Events API
export const eventsApi = {
  create: (data) => fetchWithAuth('/events/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, eventType = null, upcomingOnly = true) => {
    let url = `/events/list/${hoaId}?upcoming_only=${upcomingOnly}`;
    if (eventType) url += `&event_type=${eventType}`;
    return fetchWithAuth(url);
  },
  get: (eventId) => fetchWithAuth(`/events/${eventId}`),
  update: (eventId, data) => fetchWithAuth(`/events/${eventId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (eventId) => fetchWithAuth(`/events/${eventId}`, { method: 'DELETE' }),
  rsvp: (eventId) => fetchWithAuth(`/events/${eventId}/rsvp`, { method: 'POST' }),
  cancelRsvp: (eventId) => fetchWithAuth(`/events/${eventId}/rsvp`, { method: 'DELETE' }),
};

// Visitors API
export const visitorsApi = {
  register: (data) => fetchWithAuth('/visitors/register', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, date = null) => fetchWithAuth(`/visitors/list/${hoaId}${date ? `?date=${date}` : ''}`),
  get: (visitorId) => fetchWithAuth(`/visitors/${visitorId}`),
  verifyQR: (qrCode) => fetchWithAuth('/visitors/verify-qr', { method: 'POST', body: JSON.stringify({ qr_code: qrCode }) }),
  checkIn: (visitorId) => fetchWithAuth(`/visitors/${visitorId}/checkin`, { method: 'POST' }),
  checkOut: (visitorId) => fetchWithAuth(`/visitors/${visitorId}/checkout`, { method: 'POST' }),
  delete: (visitorId) => fetchWithAuth(`/visitors/${visitorId}`, { method: 'DELETE' }),
};

// Facilities API
export const facilitiesApi = {
  create: (data) => fetchWithAuth('/facilities/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId) => fetchWithAuth(`/facilities/list/${hoaId}`),
  get: (facilityId) => fetchWithAuth(`/facilities/${facilityId}`),
  update: (facilityId, data) => fetchWithAuth(`/facilities/${facilityId}`, { method: 'PUT', body: JSON.stringify(data) }),
  createBooking: (data) => fetchWithAuth('/facilities/bookings/create', { method: 'POST', body: JSON.stringify(data) }),
  listBookings: (hoaId, facilityId = null) => {
    let url = `/facilities/bookings/list/${hoaId}`;
    if (facilityId) url += `?facility_id=${facilityId}`;
    return fetchWithAuth(url);
  },
  getBooking: (bookingId) => fetchWithAuth(`/facilities/bookings/${bookingId}`),
  cancelBooking: (bookingId) => fetchWithAuth(`/facilities/bookings/${bookingId}`, { method: 'DELETE' }),
  getAvailability: (facilityId, date) => fetchWithAuth(`/facilities/availability/${facilityId}?date=${date}`),
};

// Parcels API
export const parcelsApi = {
  receive: (data) => fetchWithAuth('/parcels/receive', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, status = null) => fetchWithAuth(`/parcels/list/${hoaId}${status ? `?status=${status}` : ''}`),
  get: (parcelId) => fetchWithAuth(`/parcels/${parcelId}`),
  collect: (parcelId) => fetchWithAuth(`/parcels/${parcelId}/collect`, { method: 'POST' }),
  getPendingCount: (hoaId) => fetchWithAuth(`/parcels/pending/count/${hoaId}`),
};

// Marketplace API
export const marketplaceApi = {
  create: (data) => fetchWithAuth('/marketplace/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, category = null) => fetchWithAuth(`/marketplace/list/${hoaId}${category ? `?category=${category}` : ''}`),
  get: (listingId) => fetchWithAuth(`/marketplace/${listingId}`),
  update: (listingId, data) => fetchWithAuth(`/marketplace/${listingId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (listingId) => fetchWithAuth(`/marketplace/${listingId}`, { method: 'DELETE' }),
  markSold: (listingId) => fetchWithAuth(`/marketplace/${listingId}/mark-sold`, { method: 'POST' }),
};

// Chat API
export const chatApi = {
  create: (data) => fetchWithAuth('/chat/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, category = null, limit = 50) => {
    let url = `/chat/list/${hoaId}?limit=${limit}`;
    if (category) url += `&category=${category}`;
    return fetchWithAuth(url);
  },
  get: (messageId) => fetchWithAuth(`/chat/${messageId}`),
  reply: (messageId, content) => fetchWithAuth(`/chat/${messageId}/reply`, { method: 'POST', body: JSON.stringify({ content }) }),
  like: (messageId) => fetchWithAuth(`/chat/${messageId}/like`, { method: 'POST' }),
  delete: (messageId) => fetchWithAuth(`/chat/${messageId}`, { method: 'DELETE' }),
  unreadCount: (hoaId) => fetchWithAuth(`/chat/unread-count/${hoaId}`),
  markRead: (hoaId) => fetchWithAuth(`/chat/mark-read/${hoaId}`, { method: 'POST' }),
};

// Payments API
export const paymentsApi = {
  createInvoice: (data) => fetchWithAuth('/payments/invoices/create', { method: 'POST', body: JSON.stringify(data) }),
  bulkCreateInvoices: (data) => fetchWithAuth('/payments/invoices/bulk-create', { method: 'POST', body: JSON.stringify(data) }),
  listInvoices: (hoaId, status = null) => fetchWithAuth(`/payments/invoices/list/${hoaId}${status ? `?status=${status}` : ''}`),
  getInvoice: (invoiceId) => fetchWithAuth(`/payments/invoices/${invoiceId}`),
  makePayment: (invoiceId, paymentMethod = 'card') => 
    fetchWithAuth(`/payments/pay/${invoiceId}`, { method: 'POST', body: JSON.stringify({ payment_method: paymentMethod }) }),
  getHistory: (hoaId) => fetchWithAuth(`/payments/history/${hoaId}`),
  getStats: (hoaId) => fetchWithAuth(`/payments/stats/${hoaId}`),
};

// Levy Payment API with PayFast
export const levyPaymentApi = {
  initiatePayment: (data) => fetchWithAuth('/levy/payments/initiate', { 
    method: 'POST', 
    body: JSON.stringify(data) 
  }),
  getPaymentStatus: (paymentRef) => fetchWithAuth(`/levy/payments/status/${paymentRef}`),
  getLevyAccount: (accountId) => fetchWithAuth(`/levy/accounts/${accountId}`),
};

// Voting API
export const votingApi = {
  create: (data) => fetchWithAuth('/voting/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, status = null) => fetchWithAuth(`/voting/list/${hoaId}${status ? `?status=${status}` : ''}`),
  get: (voteId) => fetchWithAuth(`/voting/${voteId}`),
  cast: (voteId, selectedOption) => fetchWithAuth('/voting/cast', { 
    method: 'POST', 
    body: JSON.stringify({ vote_id: voteId, selected_option: selectedOption }) 
  }),
  close: (voteId) => fetchWithAuth(`/voting/${voteId}/close`, { method: 'POST' }),
  update: (voteId, data) => fetchWithAuth(`/voting/${voteId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (voteId) => fetchWithAuth(`/voting/${voteId}`, { method: 'DELETE' }),
  getReport: (voteId) => fetchWithAuth(`/voting/${voteId}/report`),
  getPdfReport: (voteId) => fetchWithAuth(`/voting/${voteId}/report/pdf`),
  getAuditLog: (voteId) => fetchWithAuth(`/voting/${voteId}/audit-log`),
  uploadAttachment: (hoaId, formData) => fetch(`${API_URL}/api/voting/upload-attachment/${hoaId}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` },
    body: formData
  }).then(res => res.json()),
};

// Service Providers API
export const providersApi = {
  create: (data) => fetchWithAuth('/providers/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (hoaId, serviceType = null) => fetchWithAuth(`/providers/list/${hoaId}${serviceType ? `?service_type=${serviceType}` : ''}`),
  get: (providerId) => fetchWithAuth(`/providers/${providerId}`),
  update: (providerId, data) => fetchWithAuth(`/providers/${providerId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (providerId) => fetchWithAuth(`/providers/${providerId}`, { method: 'DELETE' }),
  addReview: (providerId, rating) => fetchWithAuth(`/providers/${providerId}/review`, { method: 'POST', body: JSON.stringify({ rating }) }),
  listTypes: () => fetchWithAuth('/providers/types/list'),
};

// Agent Invitations API (Trustee to Managing Agent)
export const agentInvitationsApi = {
  invite: (data) => fetchWithAuth('/agent-invitations/invite', { method: 'POST', body: JSON.stringify(data) }),
  getSentInvitations: () => fetchWithAuth('/agent-invitations/sent'),
  getReceivedInvitations: () => fetchWithAuth('/agent-invitations/received'),
  cancel: (invitationId) => fetchWithAuth(`/agent-invitations/cancel/${invitationId}`, { method: 'DELETE' }),
  accept: (invitationId) => fetchWithAuth(`/agent-invitations/accept/${invitationId}`, { method: 'POST' }),
  decline: (invitationId, reason = null) => fetchWithAuth(`/agent-invitations/decline/${invitationId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, { method: 'POST' }),
  getStats: () => fetchWithAuth('/agent-invitations/company-stats'),
};

// Resident Invitations API
export const residentInvitationsApi = {
  create: (data) => fetchWithAuth('/invitations/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (buildingId) => fetchWithAuth(`/invitations/building/${buildingId}`),
  resend: (invitationId) => fetchWithAuth(`/invitations/${invitationId}/resend`, { method: 'POST' }),
  deactivate: (invitationId) => fetchWithAuth(`/invitations/${invitationId}/deactivate`, { method: 'POST' }),
  delete: (invitationId) => fetchWithAuth(`/invitations/${invitationId}`, { method: 'DELETE' }),
};

export default {
  hoa: hoaApi,
  notices: noticesApi,
  documents: documentsApi,
  maintenance: maintenanceApi,
  events: eventsApi,
  visitors: visitorsApi,
  facilities: facilitiesApi,
  parcels: parcelsApi,
  marketplace: marketplaceApi,
  chat: chatApi,
  payments: paymentsApi,
  voting: votingApi,
  providers: providersApi,
  agentInvitations: agentInvitationsApi,
  residentInvitations: residentInvitationsApi,
};
